import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = '/api/v1';

export interface ToolLogEntry {
  logId: string;
  callId: string;
  agentId: string;
  agentName: string;
  toolName: string;
  durationMs: number;
  success: boolean;
  error: string | null;
  createdAt: string | null;
}

export interface UseRecentToolLogsReturn {
  logs: ToolLogEntry[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRecentToolLogs(limit = 10): UseRecentToolLogsReturn {
  const { isAuthenticated } = useAuth();
  const [logs, setLogs] = useState<ToolLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchCount, setRefetchCount] = useState(0);

  const refetch = useCallback(() => setRefetchCount((n) => n + 1), []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchLogs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_BASE_URL}/dashboard/tool-executions`, {
          params: { limit },
        });
        const items = response.data?.data?.items ?? response.data?.items ?? [];
        setLogs(items);
      } catch (err: any) {
        setError(err.response?.data?.message ?? 'Failed to load recent tool logs');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogs();
  }, [isAuthenticated, limit, refetchCount]);

  return { logs, isLoading, error, refetch };
}
